import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'

// Moria's PrimeVue preset.
//
// Color discipline: the brand green signals STATUS/identity (active/done
// badges, the up-to-date medallion, selected nav, focus rings). ACTIONS read in
// the app's blue, so a primary button never collides green-on-green with the
// status around it.
//
// PrimeVue maps the `primary` button severity to the global primary color
// (our green) and the default `info` severity to `sky` (a cyan that doesn't
// match our blue chips). We repoint BOTH button severities to the app blue
// here, in the theme, so every action button is the same blue regardless of
// which severity a call site used — individual `<Button>`s don't need a
// severity to be blue. Only the `button` component is touched; the global
// `primary.*` palette stays green, so focus rings, form-field focus, selected
// states, and brand surfaces are unchanged.
//
// Values are wired to the `--action-blue*` / `--color-unseen-dot` CSS variables
// (theme.css), which are the single source of truth for the app's blue and are
// themselves light/dark-aware — so the button blue can never drift from the
// chip/dot blue, and the same var-based values work in both color schemes.
const FILLED = {
  background: 'var(--action-blue)',
  hoverBackground: 'var(--action-blue-hover)',
  activeBackground: 'var(--action-blue-active)',
  borderColor: 'var(--action-blue)',
  hoverBorderColor: 'var(--action-blue-hover)',
  activeBorderColor: 'var(--action-blue-active)',
  color: 'var(--action-blue-contrast)',
  hoverColor: 'var(--action-blue-contrast)',
  activeColor: 'var(--action-blue-contrast)',
  focusRing: { color: 'var(--action-blue)' },
} as const

const OUTLINED = {
  color: 'var(--color-unseen-dot)',
  borderColor: 'color-mix(in srgb, var(--color-unseen-dot) 40%, transparent)',
  hoverBackground: 'color-mix(in srgb, var(--color-unseen-dot) 8%, transparent)',
  activeBackground: 'color-mix(in srgb, var(--color-unseen-dot) 14%, transparent)',
} as const

const TEXT = {
  color: 'var(--color-unseen-dot)',
  hoverBackground: 'color-mix(in srgb, var(--color-unseen-dot) 8%, transparent)',
  activeBackground: 'color-mix(in srgb, var(--color-unseen-dot) 14%, transparent)',
} as const

// Same var-based values in both schemes; the CSS vars carry the light/dark split.
const SCHEME = {
  root: { primary: FILLED, info: FILLED },
  outlined: { primary: OUTLINED, info: OUTLINED },
  text: { primary: TEXT, info: TEXT },
} as const

export const MoriaAura = definePreset(Aura, {
  components: {
    button: {
      colorScheme: { light: SCHEME, dark: SCHEME },
    },
  },
})
